import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    DEFAULT_LISTING_HANDLER_EVENT_ERROR_BODY,
    Draft,
    EditListingRequest,
    ListingRequest,
    ListingRequestType,
    ListingResponse,
    LISTING_HANDLER_EVENT_ERROR_TITLE,
    LISTING_HANDLER_SUCCESSFULLY,
    REDIRECT_LISTING_PAGE,
    RelistListingRequest
} from '@classifieds-sell/datalayer';
import {
    DraftFacade,
    DraftState,
    IRouterData,
    RouterFacade,
    StepInfo,
    UserFacade
} from '@classifieds-sell/state';
import {
    addTrailingSlash,
    removeTrailingSlash,
    StepDisabledStates
} from '@classifieds-sell/shared';
import { DelayService } from '@trademe/tangram';
import { environment } from 'apps/property-sell/src/environments/environment';
import { from, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { isEditOrRelistPage } from '../../helpers/router.helper';
import { StepIds } from '../../models/property-step/property-step-model';
import { PropertyPageBase } from '../../pages/property-page-base';
import {
    DEFAULT_NOTIFICATION_DURATION,
    INotificationOptions,
    NotificationService
} from '@classifieds-sell/trademe-core-ui';

@Component({
    selector: 'property-home',
    templateUrl: './property-home.component.html',
    styleUrls: ['./property-home.component.scss'],
    standalone: false
})
export class PropertyHomeComponent implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();
    private _currentPageChange$: Subject<void> = new Subject<void>();
    private _currentPage: PropertyPageBase = null;
    private _isEditOrRelistPage: boolean;
    private _draftData: Draft;
    private _subCategory: string;
    private _listingId: number;

    private _routerData: IRouterData;

    isStepInfoReady = false;

    selectedStepId: StepIds;
    callbackInBeforeStepChange: (arg?: unknown) => void;
    callbackArgInBeforeStepChange?: unknown;

    saveAndCompleteLaterSubmitted = false;
    beforeContinueSubmitted = false;
    beforeStepChangeSubmitted = false;
    listingEndDate: Date;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _delayService: DelayService,
        private _draftFacade: DraftFacade,
        private _router: Router,
        private _routerFacade: RouterFacade,
        private _userFacade: UserFacade,
        private _notificationService: NotificationService
    ) {}

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this._isEditOrRelistPage = isEditOrRelistPage(
            this._activatedRoute,
            true
        );
        this._activatedRoute.params.subscribe((params) => {
            this._subCategory = params.subCategory;
            this._listingId = params.listingId;
        });
        this.handleListingResponse();
        from(this._routerFacade.routerData$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((routerStateData) => {
                this._routerData = routerStateData;
                if (Object.keys(this._routerData.stepInfo).length > 0) {
                    this.isStepInfoReady = true;
                }
            });

        from(this._draftFacade.state$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((draftStateData) =>
                this.draftStateSubscriber(draftStateData)
            );
    }

    onActivate(page: PropertyPageBase) {
        this._currentPage = page;
        this._currentPageChange$.next();
    }

    onContinue(stepInfo: StepInfo) {
        if (this.isPageValid()) {
            this.selectedStepId = stepInfo.nextStep.stepId as StepIds;

            this.enableNextStepAndUpdatePropertySteps();

            if (!this._currentPage.dirty()) {
                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
                return;
            }

            if (this._isEditOrRelistPage) {
                this._currentPage.saveDraft(this._listingId);

                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
                return;
            }

            this.beforeContinueSubmitted = true;
            this._currentPage.saveDraft();
        } else {
            this.displayPageInvalidWarning();
        }
    }

    beforeStepChange(
        cb: (arg?: unknown) => void,
        arg?: unknown,
        isBack?: boolean
    ) {
        const isPageValid = this.isPageValid();
        // Page validation not required when navigating back
        if (isPageValid || isBack) {
            this.callbackInBeforeStepChange = cb;
            this.callbackArgInBeforeStepChange = arg;

            // TODO: isPageValid temporary until we support saving invalid state
            if (
                !this._currentPage.dirty() ||
                !isPageValid ||
                this._isEditOrRelistPage
            ) {
                if (this._isEditOrRelistPage) {
                    this._currentPage.saveDraft(this._listingId);
                }
                this.callbackInBeforeStepChange(
                    this.callbackArgInBeforeStepChange
                );
                return;
            }

            this.beforeStepChangeSubmitted = true;
            this._currentPage.saveDraft();
        } else {
            this.displayPageInvalidWarning();
        }
    }

    onCreateListing() {
        const request: ListingRequest = {
            categoryId: this._routerData.categoryId,
            subCategory: this._routerData.subCategory,
            type: ListingRequestType.Create
        };
        this._draftFacade.makeListingAction(request);
    }

    onEditListing(): void {
        const request: EditListingRequest = {
            categoryId: this._routerData.categoryId,
            draft: this._draftData,
            listingId: this._listingId,
            subCategory: this._subCategory,
            listingEndDate: this.listingEndDate,
            type: ListingRequestType.Edit
        };
        this._draftFacade.makeListingAction(request);
    }

    public onRelistListing(): void {
        const request: RelistListingRequest = {
            categoryId: this._routerData.categoryId,
            draft: this._draftData,
            listingId: this._listingId,
            subCategory: this._subCategory,
            type: ListingRequestType.Relist
        };
        this._draftFacade.makeListingAction(request);
    }

    onTopUpCompleted() {
        this._userFacade.reloadUserDetails();
        const successNotification: INotificationOptions = {
            title: 'Top up',
            message: 'Top up successfully applied'
        };

        this._notificationService.success(successNotification);
    }

    onSaveAndCompleteLater() {
        if (this.isPageValid()) {
            this.saveAndCompleteLaterSubmitted = true;
            this._currentPage.saveDraft(this._listingId);
        } else {
            this.displayPageInvalidWarning();
        }
    }

    private handleListingResponse(): void {
        from(this._draftFacade.listingActionResponse$)
            .pipe(takeUntil(this._destroyed$), skip(1)) // ignore past/existing state
            .subscribe((listingResponse) => {
                this.handleResponseListing(
                    listingResponse.response,
                    listingResponse.type
                );
            });
    }

    private handleResponseListing(
        listingResponse: ListingResponse,
        event: string
    ): void {
        if (listingResponse.success) {
            const successNotification: INotificationOptions = {
                title: LISTING_HANDLER_SUCCESSFULLY(event),
                message: REDIRECT_LISTING_PAGE
            };

            this._notificationService.success(successNotification);
            this._delayService
                .createDelay(3500)
                .subscribe(
                    () =>
                        (window.location.href = `${addTrailingSlash(
                            environment.trademeUrl
                        )}${listingResponse.listingId}`)
                );
        } else {
            const errorNotification: INotificationOptions = {
                title: LISTING_HANDLER_EVENT_ERROR_TITLE,
                message:
                    (listingResponse.description || '').length > 0
                        ? listingResponse.description
                        : DEFAULT_LISTING_HANDLER_EVENT_ERROR_BODY
            };

            this._notificationService.error(errorNotification);
        }
    }

    private draftStateSubscriber(draftStateData: DraftState): void {
        this._draftData = draftStateData.draftData;
        if (draftStateData.loadingSuccess) {
            if (this.saveAndCompleteLaterSubmitted) {
                this.saveAndCompleteLaterSubmitted = false;
                const successNotification: INotificationOptions = {
                    title: 'Draft saved',
                    message: 'Come back here later to continue your draft'
                };

                this._notificationService.success(successNotification);
                this._delayService
                    .createDelay(3500)
                    .subscribe(
                        () =>
                            (window.location.href = removeTrailingSlash(
                                environment.trademeUrl
                            ))
                    );
            }

            if (this.beforeContinueSubmitted) {
                this.beforeContinueSubmitted = false;
                this._router.navigate([this.selectedStepId], {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
            }

            if (this.beforeStepChangeSubmitted) {
                this.beforeStepChangeSubmitted = false;
                this.callbackInBeforeStepChange(
                    this.callbackArgInBeforeStepChange
                );
            }
        }

        if (
            draftStateData.loadingFailure &&
            (this.saveAndCompleteLaterSubmitted ||
                this.beforeContinueSubmitted ||
                this.beforeStepChangeSubmitted)
        ) {
            this.saveAndCompleteLaterSubmitted = false;

            const errorNotification: INotificationOptions = {
                title: 'Your draft couldn’t be saved',
                message:
                    draftStateData.error.error?.ErrorDescription ||
                    'Please wait a few seconds and try again.'
            };

            this._notificationService.error(errorNotification);

            this._delayService
                .createDelay(DEFAULT_NOTIFICATION_DURATION)
                .subscribe(() => {
                    this._draftFacade.resetLoadingStatus();
                });
        }
        this.listingEndDate = draftStateData.metadata.listingEndDate;
    }

    private isPageValid(): boolean {
        return this._currentPage.isValid();
    }

    private displayPageInvalidWarning() {
        const errorNotification: INotificationOptions = {
            title: 'You’ve missed some things',
            message: 'Please ensure all required fields are filled correctly.'
        };

        this._notificationService.error(errorNotification);
    }

    private enableNextStepAndUpdatePropertySteps(): void {
        const temp = this._routerData.steps.map((step) => {
            if (step.stepId === this.selectedStepId) {
                return Object.assign({}, step, {
                    disabledState: StepDisabledStates.enabled
                });
            }

            return step;
        });
        this._routerFacade.updateSteps(temp);
    }
}
